import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Settings, FileText, Save, Plus, Eye, Edit2, Trash2, GripVertical, ArrowUp, ArrowDown, Type, AlignLeft, List, Image, Minus, MessageSquare, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { ImageUrlField } from '@/components/admin/ImageUrlField';

const SECTION_TYPES = [
  { value: 'hero', label: 'Hero / Page Header', icon: Type },
  { value: 'rich_text', label: 'Text Content', icon: AlignLeft },
  { value: 'faq', label: 'FAQ (Questions & Answers)', icon: MessageSquare },
  { value: 'feature_grid', label: 'Feature Cards', icon: List },
  { value: 'stats', label: 'Statistics', icon: Type },
  { value: 'cta', label: 'Call to Action', icon: ArrowUp },
  { value: 'trust_strip', label: 'Process Steps', icon: List },
];

export default function CMSEditorPage() {
  const [pages, setPages] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => { loadPages(); }, []);

  const loadPages = async () => {
    try {
      const res = await api.get('/admin/cms/pages');
      setPages(res.data);
      if (res.data.length > 0 && !selectedSlug) selectPage(res.data[0]);
    } catch { toast.error('Failed to load pages'); }
    finally { setLoading(false); }
  };

  const selectPage = (page) => {
    setSelectedSlug(page.slug);
    setSections(page.sections || []);
    setPreviewMode(false);
  };

  const handleSaveSections = async () => {
    if (!selectedSlug) return;
    setSaving(true);
    try {
      // Atomic single-request replace (CMS-01): the server stages the new set and
      // only removes the old sections once every insert succeeds, so a mid-save
      // failure never partially wipes the live page.
      await api.put(`/admin/cms/pages/${selectedSlug}/sections`, {
        sections: sections.map((s, idx) => ({
          section_type: s.section_type,
          sort_order: typeof s.sort_order === 'number' ? s.sort_order : idx,
          title: s.title || '',
          subtitle: s.subtitle || '',
          body: s.body || '',
          data: s.data || {},
          is_visible: s.is_visible !== false,
        })),
      });
      toast.success('Page saved!');
      await loadPages();
    } catch (e) { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const addSection = (type) => {
    const newSection = {
      section_type: type,
      sort_order: sections.length,
      title: '',
      subtitle: '',
      body: '',
      data: type === 'faq' ? { items: [{ question: '', answer: '' }] } :
            type === 'feature_grid' ? { items: [{ title: '', description: '', href: '' }] } :
            type === 'stats' ? { stats: [{ value: '', label: '' }] } :
            type === 'cta' ? { cta_primary: { label: '', href: '' } } :
            type === 'trust_strip' ? { steps: [{ title: '', description: '', icon: 'check' }] } : {},
      is_visible: true,
      _key: Date.now(),
    };
    setSections([...sections, newSection]);
  };

  const updateSection = (idx, field, value) => {
    const updated = [...sections];
    updated[idx] = { ...updated[idx], [field]: value };
    setSections(updated);
  };

  const removeSection = (idx) => {
    setSections(sections.filter((_, i) => i !== idx));
  };

  const moveSection = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= sections.length) return;
    const updated = [...sections];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    updated.forEach((s, i) => s.sort_order = i);
    setSections(updated);
  };

  // FAQ helpers
  const addFaqItem = (sIdx) => {
    const updated = [...sections];
    const items = [...(updated[sIdx].data?.items || [])];
    items.push({ question: '', answer: '' });
    updated[sIdx] = { ...updated[sIdx], data: { ...updated[sIdx].data, items } };
    setSections(updated);
  };
  const updateFaqItem = (sIdx, iIdx, field, value) => {
    const updated = [...sections];
    const items = [...(updated[sIdx].data?.items || [])];
    items[iIdx] = { ...items[iIdx], [field]: value };
    updated[sIdx] = { ...updated[sIdx], data: { ...updated[sIdx].data, items } };
    setSections(updated);
  };
  const removeFaqItem = (sIdx, iIdx) => {
    const updated = [...sections];
    const items = (updated[sIdx].data?.items || []).filter((_, i) => i !== iIdx);
    updated[sIdx] = { ...updated[sIdx], data: { ...updated[sIdx].data, items } };
    setSections(updated);
  };

  // Generic data helpers for nested section arrays/objects (stats, feature
  // cards, hero CTAs) so they become editable from the CMS.
  const addDataItem = (sIdx, key, template) => {
    const updated = [...sections];
    const arr = [...(updated[sIdx].data?.[key] || []), template];
    updated[sIdx] = { ...updated[sIdx], data: { ...updated[sIdx].data, [key]: arr } };
    setSections(updated);
  };
  const updateDataItem = (sIdx, key, iIdx, field, value) => {
    const updated = [...sections];
    const arr = [...(updated[sIdx].data?.[key] || [])];
    arr[iIdx] = { ...arr[iIdx], [field]: value };
    updated[sIdx] = { ...updated[sIdx], data: { ...updated[sIdx].data, [key]: arr } };
    setSections(updated);
  };
  const removeDataItem = (sIdx, key, iIdx) => {
    const updated = [...sections];
    const arr = (updated[sIdx].data?.[key] || []).filter((_, i) => i !== iIdx);
    updated[sIdx] = { ...updated[sIdx], data: { ...updated[sIdx].data, [key]: arr } };
    setSections(updated);
  };
  const updateDataObj = (sIdx, key, field, value) => {
    const updated = [...sections];
    const obj = { ...(updated[sIdx].data?.[key] || {}), [field]: value };
    updated[sIdx] = { ...updated[sIdx], data: { ...updated[sIdx].data, [key]: obj } };
    setSections(updated);
  };
  const updateDataArrayValue = (sIdx, key, iIdx, value) => {
    const updated = [...sections];
    const arr = [...(updated[sIdx].data?.[key] || [])];
    arr[iIdx] = value;
    updated[sIdx] = { ...updated[sIdx], data: { ...updated[sIdx].data, [key]: arr } };
    setSections(updated);
  };

  const selectedPage = pages.find(p => p.slug === selectedSlug);

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div data-testid="cms-editor">
      <div className="flex items-center gap-2 mb-4">
        <Settings className="h-4 w-4 text-ink" />
        <h1 className="text-base font-semibold text-ink">Content Management</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-4">
        {/* Page list */}
        <Card className="bg-white border-stone/50">
          <CardContent className="p-2">
            <h3 className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 px-2">Pages</h3>
            {pages.map((page) => (
              <button key={page.slug} onClick={() => selectPage(page)}
                className={`w-full text-left px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${selectedSlug === page.slug ? 'bg-secondary text-ink' : 'text-muted-foreground hover:text-ink hover:bg-secondary/60'}`}
                data-testid={`cms-page-item-${page.slug}`}>
                <div className="flex items-center justify-between">
                  <span>{page.title || page.seo_title || page.slug}</span>
                  {page.is_published ? <Badge variant="outline" className="text-[8px] h-4" style={{color:'#0E6B5D'}}>Live</Badge> : <Badge variant="outline" className="text-[8px] h-4">Draft</Badge>}
                </div>
                <span className="text-[10px] text-muted-foreground font-mono">/{page.slug}</span>
              </button>
            ))}
          </CardContent>
        </Card>

        {/* Editor */}
        {selectedPage ? (
          <div className="space-y-3">
            {/* Toolbar */}
            <Card className="bg-white border-stone/50">
              <CardContent className="p-3 flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-semibold text-ink">{selectedPage.seo_title || selectedPage.slug}</h2>
                  <span className="text-[10px] font-mono text-muted-foreground">/{selectedSlug}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => setPreviewMode(!previewMode)}>
                    {previewMode ? <Edit2 className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                    {previewMode ? 'Edit' : 'Preview'}
                  </Button>
                  <Button size="sm" className="h-7 text-xs gap-1" onClick={handleSaveSections} disabled={saving} data-testid="cms-save-btn">
                    <Save className="h-3 w-3" /> {saving ? 'Saving...' : 'Save Page'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {previewMode ? (
              <Card className="bg-white border-stone/50">
                <CardContent className="p-6">
                  {sections.filter(s => s.is_visible !== false).map((s, i) => (
                    <div key={i} className="mb-6">
                      {s.title && <h2 className="font-display text-xl font-medium text-ink mb-2">{s.title}</h2>}
                      {s.subtitle && <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{s.subtitle}</p>}
                      {s.body && <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{__html: s.body}} />}
                      {s.section_type === 'faq' && s.data?.items?.map((faq, j) => (
                        <div key={j} className="border-b border-stone/30 py-2"><p className="text-sm font-medium">{faq.question}</p><p className="text-xs text-muted-foreground">{faq.answer}</p></div>
                      ))}
                    </div>
                  ))}
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Sections */}
                {sections.map((section, idx) => (
                  <Card key={section._key || section.id || idx} className="bg-white border-stone/50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[9px]">{section.section_type}</Badge>
                          <span className="text-xs text-muted-foreground">Section {idx + 1}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => moveSection(idx, -1)} disabled={idx === 0}><ArrowUp className="h-3 w-3" /></Button>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => moveSection(idx, 1)} disabled={idx === sections.length - 1}><ArrowDown className="h-3 w-3" /></Button>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500" onClick={() => removeSection(idx)}><Trash2 className="h-3 w-3" /></Button>
                        </div>
                      </div>

                      {/* Common fields */}
                      <div className="space-y-2">
                        <div><Label className="text-xs">Title</Label><Input value={section.title || ''} onChange={e => updateSection(idx, 'title', e.target.value)} className="mt-1 h-8 text-xs" placeholder="Section title" /></div>
                        {(section.section_type === 'hero' || section.section_type === 'cta') && (
                          <div><Label className="text-xs">Subtitle</Label><Input value={section.subtitle || ''} onChange={e => updateSection(idx, 'subtitle', e.target.value)} className="mt-1 h-8 text-xs" placeholder="Subtitle text" /></div>
                        )}
                        {(section.section_type === 'hero' || section.section_type === 'rich_text' || section.section_type === 'cta') && (
                          <div>
                            <Label className="text-xs">Content</Label>
                            <textarea value={section.body || ''} onChange={e => updateSection(idx, 'body', e.target.value)}
                              className="w-full mt-1 p-2 rounded border border-stone text-xs min-h-[80px] resize-y focus:outline-none focus:ring-1 focus:ring-ring/30"
                              placeholder="Write your content here. You can use basic HTML tags like <h3>, <p>, <strong>, <ul>, <li> for formatting." />
                            <p className="text-[10px] text-muted-foreground mt-1">Tip: Use &lt;h3&gt; for subheadings, &lt;p&gt; for paragraphs, &lt;strong&gt; for bold, &lt;ul&gt;&lt;li&gt; for lists</p>
                          </div>
                        )}

                        {/* FAQ sections */}
                        {section.section_type === 'faq' && (
                          <div className="space-y-2">
                            <Label className="text-xs">Questions & Answers</Label>
                            {(section.data?.items || []).map((faq, fIdx) => (
                              <div key={fIdx} className="p-2 rounded bg-stone/5 border border-stone/20 space-y-1">
                                <div className="flex items-center gap-1">
                                  <Input value={faq.question} onChange={e => updateFaqItem(idx, fIdx, 'question', e.target.value)} className="h-7 text-xs flex-1" placeholder="Question" />
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => removeFaqItem(idx, fIdx)}><Trash2 className="h-3 w-3" /></Button>
                                </div>
                                <textarea value={faq.answer} onChange={e => updateFaqItem(idx, fIdx, 'answer', e.target.value)}
                                  className="w-full p-1.5 rounded border border-stone/30 text-xs min-h-[40px] resize-y focus:outline-none" placeholder="Answer" />
                              </div>
                            ))}
                            <Button variant="outline" size="sm" className="text-xs gap-1 w-full" onClick={() => addFaqItem(idx)}><Plus className="h-3 w-3" /> Add Q&A</Button>
                          </div>
                        )}

                        {/* CTA button config */}
                        {section.section_type === 'cta' && (
                          <div className="grid grid-cols-2 gap-2">
                            <div><Label className="text-xs">Button Text</Label><Input value={section.data?.cta_primary?.label || ''} onChange={e => updateSection(idx, 'data', {...(section.data||{}), cta_primary: {...(section.data?.cta_primary||{}), label: e.target.value}})} className="mt-1 h-7 text-xs" placeholder="Get Started" /></div>
                            <div><Label className="text-xs">Button Link</Label><Input value={section.data?.cta_primary?.href || ''} onChange={e => updateSection(idx, 'data', {...(section.data||{}), cta_primary: {...(section.data?.cta_primary||{}), href: e.target.value}})} className="mt-1 h-7 text-xs" placeholder="/services" /></div>
                          </div>
                        )}

                        {/* Hero buttons (e.g. "Choose Your Service") */}
                        {section.section_type === 'hero' && (
                          <div className="space-y-2">
                            <Label className="text-xs">Buttons</Label>
                            <div className="grid grid-cols-2 gap-2">
                              <div><Label className="text-[10px] text-muted-foreground">Primary text</Label><Input value={section.data?.cta_primary?.label || ''} onChange={e => updateDataObj(idx, 'cta_primary', 'label', e.target.value)} className="mt-1 h-7 text-xs" placeholder="Choose Your Service" data-testid={`cms-hero-cta1-label-${idx}`} /></div>
                              <div><Label className="text-[10px] text-muted-foreground">Primary link</Label><Input value={section.data?.cta_primary?.href || ''} onChange={e => updateDataObj(idx, 'cta_primary', 'href', e.target.value)} className="mt-1 h-7 text-xs" placeholder="/services" /></div>
                              <div><Label className="text-[10px] text-muted-foreground">Secondary text</Label><Input value={section.data?.cta_secondary?.label || ''} onChange={e => updateDataObj(idx, 'cta_secondary', 'label', e.target.value)} className="mt-1 h-7 text-xs" placeholder="Verify a Certificate" /></div>
                              <div><Label className="text-[10px] text-muted-foreground">Secondary link</Label><Input value={section.data?.cta_secondary?.href || ''} onChange={e => updateDataObj(idx, 'cta_secondary', 'href', e.target.value)} className="mt-1 h-7 text-xs" placeholder="/verify" /></div>
                            </div>
                          </div>
                        )}

                        {/* Hero image grid (2x2 layout on the public site) */}
                        {section.section_type === 'hero' && (
                          <div className="space-y-1.5">
                            <Label className="text-xs">Hero Images (2x2 grid)</Label>
                            {[0, 1, 2].map(imgIdx => (
                              <ImageUrlField key={imgIdx}
                                value={(section.data?.images || [])[imgIdx] || ''}
                                onChange={v => updateDataArrayValue(idx, 'images', imgIdx, v)}
                                placeholder={`Grid image ${imgIdx + 1} URL`}
                                testId={`cms-hero-image-${idx}-${imgIdx}`} />
                            ))}
                            <p className="text-[10px] text-muted-foreground">The 4th grid tile shows the "Verified Authentic" badge.</p>
                          </div>
                        )}

                        {/* Statistics (e.g. "Trusted by Collectors Worldwide") */}
                        {section.section_type === 'stats' && (
                          <div className="space-y-2">
                            <Label className="text-xs">Statistics</Label>
                            {(section.data?.stats || []).map((stat, stIdx) => (
                              <div key={stIdx} className="flex items-center gap-1 p-2 rounded bg-stone/5 border border-stone/20">
                                <Input value={stat.value || ''} onChange={e => updateDataItem(idx, 'stats', stIdx, 'value', e.target.value)} className="h-7 text-xs w-28" placeholder="50,000+" data-testid={`cms-stat-value-${idx}-${stIdx}`} />
                                <Input value={stat.label || ''} onChange={e => updateDataItem(idx, 'stats', stIdx, 'label', e.target.value)} className="h-7 text-xs flex-1" placeholder="Items Authenticated" />
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => removeDataItem(idx, 'stats', stIdx)}><Trash2 className="h-3 w-3" /></Button>
                              </div>
                            ))}
                            <Button variant="outline" size="sm" className="text-xs gap-1 w-full" onClick={() => addDataItem(idx, 'stats', { value: '', label: '' })}><Plus className="h-3 w-3" /> Add Stat</Button>
                          </div>
                        )}

                        {/* Feature cards + their buttons/links */}
                        {section.section_type === 'feature_grid' && (
                          <div className="space-y-2">
                            <Label className="text-xs">Cards & Buttons</Label>
                            {(section.data?.items || []).map((it, fIdx) => (
                              <div key={fIdx} className="p-2 rounded bg-stone/5 border border-stone/20 space-y-1">
                                <div className="flex items-center gap-1">
                                  <Input value={it.title || ''} onChange={e => updateDataItem(idx, 'items', fIdx, 'title', e.target.value)} className="h-7 text-xs flex-1" placeholder="Card title" data-testid={`cms-feature-title-${idx}-${fIdx}`} />
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => removeDataItem(idx, 'items', fIdx)}><Trash2 className="h-3 w-3" /></Button>
                                </div>
                                <Input value={it.description || ''} onChange={e => updateDataItem(idx, 'items', fIdx, 'description', e.target.value)} className="h-7 text-xs" placeholder="Description" />
                                <Input value={it.href || ''} onChange={e => updateDataItem(idx, 'items', fIdx, 'href', e.target.value)} className="h-7 text-xs" placeholder="Button link e.g. /services/authentication" />
                                <ImageUrlField value={it.image || ''} onChange={v => updateDataItem(idx, 'items', fIdx, 'image', v)} testId={`cms-feature-image-${idx}-${fIdx}`} />
                              </div>
                            ))}
                            <Button variant="outline" size="sm" className="text-xs gap-1 w-full" onClick={() => addDataItem(idx, 'items', { title: '', description: '', href: '' })}><Plus className="h-3 w-3" /> Add Card</Button>
                          </div>
                        )}

                        {/* Process steps (How It Works) — title, copy + image per step */}
                        {section.section_type === 'trust_strip' && (
                          <div className="space-y-2">
                            <Label className="text-xs">Steps (title, copy & image — icons are kept)</Label>
                            {(section.data?.steps || []).map((step, stIdx) => (
                              <div key={stIdx} className="p-2 rounded bg-stone/5 border border-stone/20 space-y-1">
                                <div className="flex items-center gap-1">
                                  <Input value={step.title || ''} onChange={e => updateDataItem(idx, 'steps', stIdx, 'title', e.target.value)} className="h-7 text-xs flex-1" placeholder="Step title" data-testid={`cms-step-title-${idx}-${stIdx}`} />
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => removeDataItem(idx, 'steps', stIdx)}><Trash2 className="h-3 w-3" /></Button>
                                </div>
                                <textarea value={step.description || ''} onChange={e => updateDataItem(idx, 'steps', stIdx, 'description', e.target.value)}
                                  className="w-full p-1.5 rounded border border-stone/30 text-xs min-h-[40px] resize-y focus:outline-none" placeholder="Step copy" data-testid={`cms-step-copy-${idx}-${stIdx}`} />
                                <ImageUrlField value={step.image || ''} onChange={v => updateDataItem(idx, 'steps', stIdx, 'image', v)} testId={`cms-step-image-${idx}-${stIdx}`} />
                              </div>
                            ))}
                            <Button variant="outline" size="sm" className="text-xs gap-1 w-full" onClick={() => addDataItem(idx, 'steps', { title: '', description: '', icon: 'check' })}><Plus className="h-3 w-3" /> Add Step</Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {/* Add section */}
                <Card className="bg-white/50 border-dashed border-stone/50">
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground mb-2">Add a content section:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {SECTION_TYPES.map(st => {
                        const Icon = st.icon;
                        return (
                          <Button key={st.value} variant="outline" size="sm" className="text-[10px] h-7 gap-1" onClick={() => addSection(st.value)}>
                            <Icon className="h-3 w-3" /> {st.label}
                          </Button>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        ) : (
          <Card className="bg-white border-stone/50">
            <CardContent className="p-8 text-center">
              <FileText className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">Select a page to edit</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
